/*
Copyright (c) 2026 Goffy. All Rights Reserved.
*/

export type MediaKind = 'images' | 'video';
export type BuildMediaUrlOptions = {
  pagePath?: string;
  width?: number;
  height?: number;
  bucketStep?: number;
};

const DEFAULT_DIMENSION_BUCKET_STEP = 64;
const MIN_REQUESTED_DIMENSION = 16;
const MAX_REQUESTED_DIMENSION = 4096;

const DEFAULT_MEDIA_RELEASE_VERSION = (process.env.EXPO_PUBLIC_MEDIA_RELEASE_VERSION ?? '').trim();
const DEFAULT_MEDIA_ENV = normalizeMediaEnv(
  process.env.EXPO_PUBLIC_MEDIA_ENV ?? process.env.EXPO_UPLOAD_ENV ?? process.env.NODE_ENV ?? 'prod'
);
const DEFAULT_MEDIA_BASE_URL = process.env.EXPO_PUBLIC_FRONTEND_ASSET_BASE_URL
  ?? `https://assets-${DEFAULT_MEDIA_ENV}.goffy.app`;

export function buildMediaUrl(kind: MediaKind, key: string, preset: string, options?: BuildMediaUrlOptions): string {
  const normalizedKey = normalizeMediaKey(key, kind);
  const base = resolveMediaBaseUrl();
  const encodedKey = normalizedKey
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  const params = new URLSearchParams();
  const normalizedPreset = (preset || '').trim();
  if (normalizedPreset.length > 0) {
    params.set('preset', normalizedPreset);
  }
  const pagePath = normalizeMediaRequestPagePath(options?.pagePath ?? resolveCurrentPagePath());
  if (kind === 'images') {
    params.set('pagePath', pagePath || '/unknown');
    const dimensions = normalizeRequestedDimensions(options?.width, options?.height, options?.bucketStep);
    if (dimensions.width) {
      params.set('width', dimensions.width.toString());
    }
    if (dimensions.height) {
      params.set('height', dimensions.height.toString());
    }
  }
  const releaseVersion = resolveMediaReleaseVersion();
  if (releaseVersion) {
    params.set('v', releaseVersion);
  }
  const query = params.toString();
  return query
    ? `${base}/media/${kind}/${encodedKey}?${query}`
    : `${base}/media/${kind}/${encodedKey}`;
}

export function normalizeRequestedDimensions(
  width?: number,
  height?: number,
  bucketStep = DEFAULT_DIMENSION_BUCKET_STEP,
): { width?: number; height?: number } {
  const normalizedBucketStep = Number.isFinite(bucketStep) && bucketStep > 0
    ? Math.floor(bucketStep)
    : DEFAULT_DIMENSION_BUCKET_STEP;

  return {
    width: normalizeRequestedDimensionValue(width, normalizedBucketStep),
    height: normalizeRequestedDimensionValue(height, normalizedBucketStep),
  };
}

/**
 * The CloudFront host for frontend assets, or null when none is configured.
 *
 * Callers that build a BFF image URL use this to route through the CDN instead of hitting
 * the origin directly. Returning null rather than falling back to `assets-<env>.goffy.app`
 * is deliberate: an unset value means "no CDN here" (Jest, localhost, native production),
 * and those callers must keep using the API base they were given.
 *
 * Read via the literal `process.env.X` form — `process.env?.X` is not inlined by
 * babel-preset-expo and silently resolves to undefined on native.
 */
export function getConfiguredAssetBaseUrl(): string | null {
  const configured = (process.env.EXPO_PUBLIC_FRONTEND_ASSET_BASE_URL ?? '').trim();
  return configured ? configured.replace(/\/+$/, '') : null;
}

export function normalizeMediaRequestPagePath(value?: string): string {
  const input = (value ?? '').trim();
  if (!input) {
    return '';
  }

  const withoutQuery = input.split('?')[0]?.split('#')[0] ?? '';
  const rawSegments = withoutQuery.split('/').filter(Boolean);
  if (rawSegments.length === 0) {
    return '';
  }

  const normalizedSegments = rawSegments.map((segment, index, allSegments) => {
    if (segment.startsWith('[') && segment.endsWith(']')) {
      return segment;
    }

    const previous = index > 0 ? allSegments[index - 1].toLowerCase() : '';
    if (looksLikeUuid(segment)) {
      if (previous === 'round') return '[roundId]';
      if (previous === 'tour') return '[tourId]';
      if (previous === 'player' || previous === 'players') return '[playerId]';
      if (previous === 'game' || previous === 'games') return '[gameId]';
      if (previous === 'invite') return '[inviteToken]';
      return '[id]';
    }

    if (/^\d+$/.test(segment)) {
      if (previous === 'hole') return '[hole]';
      return '[id]';
    }

    return segment;
  });

  return `/${normalizedSegments.join('/')}`;
}

function resolveMediaBaseUrl(): string {
  if (process.env.EXPO_PUBLIC_FRONTEND_ASSET_BASE_URL) {
    return process.env.EXPO_PUBLIC_FRONTEND_ASSET_BASE_URL.replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location) {
    const hostname = (window.location.hostname || '').toLowerCase();
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      const apiBase = (process.env.EXPO_PUBLIC_API_BASE_URL ?? '').trim();
      if (apiBase) {
        return apiBase.replace(/\/+$/, '');
      }

      const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
      return `${protocol}://localhost:8000`;
    }
  }

  return DEFAULT_MEDIA_BASE_URL.replace(/\/+$/, '');
}

function resolveCurrentPagePath(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  return window.location?.pathname ?? '';
}

function resolveMediaReleaseVersion(): string {
  if (process.env.EXPO_PUBLIC_MEDIA_RELEASE_VERSION) {
    return process.env.EXPO_PUBLIC_MEDIA_RELEASE_VERSION.trim();
  }

  if (process.env.EXPO_PUBLIC_APP_VERSION) {
    return process.env.EXPO_PUBLIC_APP_VERSION.trim();
  }

  return DEFAULT_MEDIA_RELEASE_VERSION;
}

function normalizeRequestedDimensionValue(value?: number, bucketStep = DEFAULT_DIMENSION_BUCKET_STEP): number | undefined {
  if (!Number.isFinite(value)) {
    return undefined;
  }

  const integerValue = Math.floor(value as number);
  if (integerValue <= 0) {
    return undefined;
  }

  const clamped = clamp(integerValue, MIN_REQUESTED_DIMENSION, MAX_REQUESTED_DIMENSION);
  const bucketed = Math.ceil(clamped / bucketStep) * bucketStep;
  return clamp(bucketed, MIN_REQUESTED_DIMENSION, MAX_REQUESTED_DIMENSION);
}

function normalizeMediaEnv(value: string): string {
  const normalized = (value ?? '').trim().toLowerCase();
  if (!normalized) {
    return 'prod';
  }

  if (normalized === 'production') {
    return 'prod';
  }

  if (normalized === 'development') {
    return 'dev';
  }

  return normalized;
}

function normalizeMediaKey(value: string, kind: MediaKind): string {
  const cleaned = (value ?? '').trim().replace(/\\/g, '/').replace(/^\/+/, '');
  if (!cleaned) {
    return kind === 'images' ? 'unknown.png' : 'unknown.mp4';
  }

  const withoutGenerated = cleaned.startsWith('assets-generated/')
    ? cleaned.slice('assets-generated/'.length)
    : cleaned;

  const withoutSource = withoutGenerated.startsWith('assets-src/')
    ? withoutGenerated.slice('assets-src/'.length)
    : withoutGenerated;

  if (kind === 'images' && withoutSource.startsWith('images/')) {
    return withoutSource.slice('images/'.length);
  }

  if (kind === 'video' && withoutSource.startsWith('video/')) {
    return withoutSource.slice('video/'.length);
  }

  return withoutSource;
}

function looksLikeUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
